import { useCallback } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import { exportCsv } from "./exportCsv";
import { exportExcel } from "./exportExcel";
import { logExport } from "./auditExport";
import { getExportColumns, getExportFileName, type EntityType } from "./columns";

export type ExportFormat = "csv" | "excel" | "print";

interface ExportActionConfig {
  entityType: EntityType;
  /** 
   * Whether this is a customer portal export.
   * Customer exports are only enabled for explicitly approved entities.
   */
  isCustomerPortal?: boolean;
}

/** Entities explicitly approved for customer portal export */
const CUSTOMER_EXPORTABLE: Set<EntityType> = new Set(["invoices"]);

export function useExportActions(config: ExportActionConfig) {
  const { isCustomer, isVendor, isPersonnel, isContractLabor } = usePermissions();
  const isExternalUser = isCustomer || isVendor || isPersonnel || isContractLabor;
  const portalMode = config.isCustomerPortal || isCustomer;

  // Determine available formats
  const availableFormats: ExportFormat[] = [];

  if (portalMode) {
    // Only allow export for explicitly approved entities
    if (CUSTOMER_EXPORTABLE.has(config.entityType)) {
      availableFormats.push("csv", "excel", "print");
    } else {
      availableFormats.push("print"); // print-only for non-approved
    }
  } else if (isVendor || isPersonnel || isContractLabor) {
    // External non-customer: print only, no financial exports
    availableFormats.push("print");
  } else {
    // Internal users: all formats
    availableFormats.push("csv", "excel", "print");
  }

  const columns = getExportColumns(config.entityType, {
    isCustomerPortal: portalMode,
  });

  const doExport = useCallback(
    (format: ExportFormat, data: any[], filterDescription?: string) => {
      const fileName = getExportFileName(config.entityType);

      if (format === "csv") {
        exportCsv(data, columns, fileName);
        logExport({
          entityType: config.entityType,
          exportType: "csv",
          recordCount: data.length,
          filters: filterDescription,
        });
      } else if (format === "excel") {
        exportExcel(data, columns, fileName);
        logExport({
          entityType: config.entityType,
          exportType: "excel",
          recordCount: data.length,
          filters: filterDescription,
        });
      } else if (format === "print") {
        window.print();
      }
    },
    [config.entityType, columns]
  );

  return {
    availableFormats,
    columns,
    doExport,
    isExternalUser,
  };
}
